import { BREAKPOINT_MOBILE } from '../../constants';
import { ElementHelper } from '../../helpers/ElementHelper';
import { MenuService } from '../../services/MenuService';
import { MegaMenuItem } from '../../types/MegaMenuItem';
import './styles.css';

export class MegaMenu {

    private static readonly HOVER_DEBOUNCE_TIME = 200;

    private readonly menuElement: HTMLElement;
    private readonly dropdownElement: HTMLElement;
    private readonly dropdownContainer: HTMLElement;
    private readonly links: HTMLAnchorElement[];
    private readonly disablers: HTMLAnchorElement[];
    private readonly breakpointQuery: MediaQueryList;
    private activePath?: string;
    private abortController?: AbortController;

    constructor(menuSelector: string, dropdownSelector: string) {
        this.menuElement = document.querySelector(menuSelector) as HTMLElement;
        this.dropdownContainer = document.querySelector(dropdownSelector) as HTMLElement;

        if (!this.menuElement)
            throw new Error(`Element with selector ${menuSelector} not found`);

        if (!this.dropdownContainer)
            throw new Error(`Element with selector ${dropdownSelector} not found`);

        const linkElements = this.menuElement.querySelectorAll('ul li[data-has-mega-menu="True"] a');
        const disablers = this.menuElement.querySelectorAll('ul li:not([data-has-mega-menu="True"]) a');

        this.links = Array.from(linkElements) as HTMLAnchorElement[];
        this.disablers = Array.from(disablers) as HTMLAnchorElement[];

        this.dropdownElement = document.createElement('div');
        this.dropdownElement.classList.add('megamenu-dropdown');
        this.dropdownContainer.after(this.dropdownElement);

        this.breakpointQuery = window.matchMedia(`(min-width: ${BREAKPOINT_MOBILE + 1}px)`);
        this.breakpointQuery.addEventListener('change', this.onToggleDropdown);

        this.toggleDropdown(this.breakpointQuery.matches);
    }

    private registerDropdown = () => {
        this.dropdownElement.addEventListener('mouseleave', this.onLeave);

        for (const link of this.links) {
            link.addEventListener('mouseenter', this.onLinkHoverDebounce);
        }
        for (const link of this.disablers) {
            link.addEventListener('mouseenter', this.onHover);
        }
    }

    private destroyDropdown = () => {
        this.dropdownElement.classList.remove('active');
        this.dropdownElement.removeEventListener('mouseleave', this.onLeave);

        for (const link of this.links) {
            link.removeEventListener('mouseenter', this.onLinkHoverDebounce);
        }
        for (const link of this.disablers) {
            link.removeEventListener('mouseenter', this.onHover);
        }        
    }
    
    private toggleDropdown = (matches: boolean) => {
        matches ? this.registerDropdown() : this.destroyDropdown();
    }

    private onToggleDropdown = (event: MediaQueryListEvent) => {
        this.toggleDropdown(event.matches);
    }

    private onLinkHoverDebounce = async (event: MouseEvent) => {
        const element = event.target as HTMLElement;
        let timeout = setTimeout(() => {
            this.onLinkHover(event);
            element.removeEventListener('mouseleave', clearDebounce);
        }, MegaMenu.HOVER_DEBOUNCE_TIME);
        const clearDebounce = () => {
            clearTimeout(timeout);
        }
        element.addEventListener('mouseleave', clearDebounce);
    }

    private onLinkHover = async(event: MouseEvent) => {
        const path = this.getAnchorPath(event);
        if (path == this.activePath) {
            return;
        }
        this.activePath = path;
        this.abortController?.abort();

        const prevWrapper = this.dropdownElement.querySelector('ul');
        prevWrapper?.classList.remove('active');
        this.dropdownElement.classList.add('loading');
        setTimeout(() => prevWrapper?.remove(), 300);
        
        this.abortController = new AbortController();
        const items = await this.getMenuItems(event);
        const aborted = this.abortController?.signal.aborted;
        delete this.abortController;

        this.dropdownElement.classList.remove('loading');
        if (aborted || !items || items.length < 1) {
            return;
        }
        this.dropdownElement.classList.add('active');
        this.populateDropdown(items);
    }
    
    private onHover = (event: MouseEvent) => {
        const path = this.getAnchorPath(event);
        if (path == this.activePath) {
            return;
        }
        this.activePath = path;
        this.abortController?.abort();
        this.dropdownElement.classList.remove('active');

        const prevWrapper = this.dropdownElement.querySelector('ul');
        prevWrapper?.classList.remove('active');
        setTimeout(() => prevWrapper?.remove(), 1300);
    }

    private onLeave = (event: MouseEvent) => {
        const rect = this.dropdownElement.getBoundingClientRect();
        const below = event.clientY > rect.bottom;
        if (!below) return;

        this.activePath = '';
        this.abortController?.abort();
        this.dropdownElement.classList.remove('active');
        const prevWrapper = this.dropdownElement.querySelector('ul');
        prevWrapper?.classList.remove('active');
    }

    private getMenuItems = async (event: MouseEvent) => {
        const anchor = event.target as HTMLAnchorElement;
        let data = ElementHelper.getCachedItems<MegaMenuItem[]>(anchor, 'data-menu');
        if (data) return data;
        
        const path = ElementHelper.getAnchorPath(anchor);
        const items = await MenuService.getMenuItems(path, this.abortController?.signal);
        ElementHelper.cacheItems(anchor, 'data-menu', items);

        return items;
    }

    private getAnchorPath = (event: MouseEvent) => {
        const target = event.target as HTMLAnchorElement;
        return ElementHelper.getAnchorPath(target);
    }

    private populateDropdown = (items: MegaMenuItem[]) => {
        const wrapper = document.createElement('ul');
        
        for (const item of items) {
            const linkElement = this.createLinkElement(item);
            wrapper.appendChild(linkElement);
        }

        this.dropdownElement.prepend(wrapper);
        setTimeout(() => wrapper.classList.add('active'), 10);
        
    }

    private createLinkElement = (item: MegaMenuItem): HTMLLIElement => {
        const element = document.createElement('li');

        const link = document.createElement('a');
        link.textContent = item.name;
        link.href = item.url ?? "#";
        
        if (item.disabled) {
            link.classList.add('disabled');
        }
        
        element.appendChild(link);

        if (!item.children || item.children.length === 0)
            return element as HTMLLIElement;

        const wrapper = document.createElement('ul');
        
        for (const child of item.children) {
            const childElement = this.createLinkElement(child);
            wrapper.appendChild(childElement);
        }

        if (item.children.length > 2) {
            wrapper.classList.add('expand');

            if (!item.disabled) {
                wrapper.addEventListener('mouseenter', this.onExpand)
                wrapper.addEventListener('mouseleave', this.onContract)
            }
        }

        element.appendChild(wrapper);        

        return element as HTMLLIElement;
    }

    private onExpand = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        target.classList.add('focus');
    }

    private onContract = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        target.classList.remove('focus');
    }

    private insideRect = (event: MouseEvent, bounds: DOMRect) => {
        return event.clientY < bounds.bottom && event.clientY > bounds.top && event.clientX > bounds.left && event.clientX < bounds.right;
    }
}